import React, { useState, useCallback, useMemo } from 'react';
import { StyleSheet, View, Pressable, LayoutAnimation, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const FAQ_ITEMS = [
  {
    q: 'Is this a recurring charge?',
    a: "No — each credit purchase is a one-time charge. If you'd prefer automatic refills, you can enable auto top-up separately in your settings.",
    icon: 'receipt',
  },
  {
    q: 'Do credits ever expire?',
    a: "Never. Purchased credits stay in your account forever. Use them today or months from now — they're yours.",
    icon: 'infinity',
  },
  {
    q: 'What if I run out mid-conversation?',
    a: 'Your conversation stays exactly where you left it. Top up and keep going — nothing is lost.',
    icon: 'comments',
  },
  {
    q: 'Is my payment information safe?',
    a: 'Fully encrypted and processed through our payment processors. We never see or store your card details.',
    icon: 'lock',
  },
  {
    q: 'Can I pay with Bitcoin?',
    a: 'Yes — on the web you can buy any credit pack with Bitcoin. Tap “pay with Bitcoin” under a pack, complete the invoice, and credits are added as soon as the payment confirms.',
    icon: 'bitcoin-sign',
  },
  {
    q: 'Why do some models cost more credits?',
    a: 'Models have different base prices. Open the price information on a model card to learn what affects its cost. Your final charge depends on actual usage and selected options.',
    icon: 'microchip',
  },
  {
    q: 'Which credits get used first?',
    a: "Your free monthly credits are always used first. Purchased credits are only touched once those run out — so you get the most out of your plan.",
    icon: 'layer-group',
  },
  {
    q: 'Can I share credits with someone?',
    a: "Credits are tied to your account and can't be transferred. But you can refer friends — you both earn free credits when they sign up.",
    icon: 'user-group',
  },
];

function FAQItem({ question, answer, icon, isOpen, onToggle, isLast, colors }) {
  const styles = useMemo(() => itemStyles(colors, isLast), [colors, isLast]);

  return (
    <View style={styles.wrapper}>
      <Pressable
        onPress={onToggle}
        style={({ hovered }) => [
          styles.question,
          hovered && styles.questionHovered,
        ]}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
      >
        <Icon name={icon} size={16} color={colors.text_color_secondary} style={styles.icon} />
        <Text size="l" weight="medium" color="primary" style={styles.questionText}>
          {question}
        </Text>
        <Icon
          name={isOpen ? 'chevron-up' : 'chevron-down'}
          size={14}
          color={colors.text_color_secondary}
        />
      </Pressable>
      {isOpen && (
        <View style={styles.answer}>
          <Text size="l" color="primary" style={styles.answerText}>
            {answer}
          </Text>
        </View>
      )}
    </View>
  );
}

const itemStyles = (colors, isLast) => StyleSheet.create({
  wrapper: {
    borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
    borderBottomColor: colors.border_color_secondary,
  },
  question: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 0,
    gap: 10,
  },
  questionHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
  icon: {
    width: 18,
    textAlign: 'center',
  },
  questionText: {
    flex: 1,
    lineHeight: 24,
  },
  answer: {
    paddingBottom: 16,
    paddingLeft: 28,
    paddingRight: 2,
  },
  answerText: {
    lineHeight: 26,
  },
});

function CreditsFAQs() {
  const { colors } = useTheme();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = useCallback((idx) => {
    if (Platform.OS !== 'web') {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    setOpenIndex(prev => (prev === idx ? null : idx));
  }, []);

  const styles = useMemo(() => createStyles(colors), [colors]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon name="shield-check" size={18} color={colors.text_color_primary} />
        <Text size="l" weight="bold" color="primary">
          Common questions
        </Text>
      </View>
      <View style={styles.list}>
        {FAQ_ITEMS.map((item, i) => (
          <FAQItem
            key={i}
            question={item.q}
            answer={item.a}
            icon={item.icon}
            isOpen={openIndex === i}
            onToggle={() => toggle(i)}
            isLast={i === FAQ_ITEMS.length - 1}
            colors={colors}
          />
        ))}
      </View>
    </View>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    gap: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 0,
  },
  list: {
    marginTop: 5,
    borderRadius: 0,
    overflow: 'hidden',
  },
});

export default React.memo(CreditsFAQs);
